import { Alert, AlertIcon, Button, Flex, Heading, theme, VStack } from '@chakra-ui/react';
import { AnimatedCheckmark } from '@common/components/AnimatedCheckmark';
import { confetti } from '@common/components/Confetti';
import { useBankAccountsModel } from '@common/models/BankAccountsModel';
import { useConsolidatedModel } from '@common/models/ConsolidatedModel';
import { usePricingPlanModel } from '@common/models/PricingPlanModel';
import { FontFamily } from '@common/theming/Fonts';
import currency from '@common/utility/currency';
import { FrequencyMap } from '@services/appsync/mappings';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { UI } from 'src/apps/invest/common/shells/UI';
import { ROUTES } from 'src/apps/invest/config';
import { useCheckoutCashflowViewModel } from '../../models/CheckoutCashflowViewModel';
import Summary from '../components/Summary';
import { RegulatoryRuleType } from '@services/appsync/types';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';

export const CheckoutCashflowCongratsPage: React.FC = () => {
  const viewModel = useCheckoutCashflowViewModel();
  const bankAccountsModel = useBankAccountsModel();
  const bankAccount = bankAccountsModel.getDefaultBankAccount();
  const pricingPlanModel = usePricingPlanModel();
  const consolidatedModel = useConsolidatedModel();
  const investorModel = useInvestorAccountModel();
  const isRegD = investorModel.subscriptionOffer?.regulatory_rule_type === RegulatoryRuleType.REG_D_506C;

  const init = async () => {
    confetti();

    // Update account data
    await consolidatedModel.fetchData();
  };

  useEffect(() => {
    init();
  }, []);

  return (
    <UI>
      <VStack width="full" justifyContent={'center'} gap="20px" padding={['0px', '50px']} maxW="960px" margin="0 auto">
        <Heading
          fontSize={['32px', '32px', '32px', '48px']}
          fontFamily={FontFamily.HELVETICA_NOW_BOLD}
          fontWeight="normal"
          textAlign="center">
          Congrats on your <br />
          {currency.format(viewModel.amount)} investment!
        </Heading>
        <AnimatedCheckmark fill="transparent" strokeColor={theme.colors.blue[500]} strokeWidth={6} size={123} />
        <Alert status="info">
          <AlertIcon />
          {viewModel.frequency === FrequencyMap.ONE
            ? 'We will notify you once it is approved and we transfer the money from your bank.'
            : 'We will send you a reminder before your next scheduled investment starts.'}
        </Alert>
        <Summary.Container label="Investment Summary">
          <Summary.Item label="Offering" value={isRegD ? 'Cash Flow (Accredited)' : 'Cash Flow'} />
          <Summary.Item label="Amount" value={currency.format(viewModel.amount)} />
          <Summary.Item label="Frequency" value={viewModel.getFrequencyDescription()} />
          <Summary.Item
            label="Account"
            value={`${bankAccount?.name} - ${bankAccount?.account_type} (...${bankAccount?.account_number || ''})`}
          />
          {viewModel.pricingPlan && (
            <Summary.Item label="Pricing Plan" value={pricingPlanModel.getFriendlyName(viewModel.pricingPlan)} />
          )}
        </Summary.Container>
        <TextBlock opacity={0.65}>What would you like to do next?</TextBlock>
        <Flex
          flexDirection={['column', 'column', 'column', 'row']}
          justifyContent={['center', 'center', 'flex-start']}
          alignItems={['center', 'center', 'flex-start']}
          gap="20px">
          <Button as={Link} to={ROUTES.dashboard} size="md">
            Back To Dashboard
          </Button>
          <Button as={Link} to={ROUTES.activity} size="md" variant={'outline'}>
            See My Activity
          </Button>
          <Button as={Link} to={ROUTES.autoInvestSchedule} size="md" variant={'outline'}>
            See My Auto Investments
          </Button>
        </Flex>
      </VStack>
    </UI>
  );
};
